import {
  customElement,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';

import { DeesChartEchartsBase } from '../dees-chart-echarts-base.js';
import { demoFunc } from './demo.js';
import { donutStyles } from './styles.js';
import { renderChartDonut } from './template.js';
import { getEchartsSeriesColors, getThemeColors, hexToRgba } from '../dees-chart-echarts-theme.js';

export interface IDonutDataItem {
  name: string;
  value: number;
  color?: string;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-chart-donut': DeesChartDonut;
  }
}

@customElement('dees-chart-donut')
export class DeesChartDonut extends DeesChartEchartsBase {
  public static demo = demoFunc;
  public static demoGroups = ['Chart'];

  @property({ type: Array })
  accessor data: IDonutDataItem[] = [];

  @property({ type: Boolean })
  accessor showLegend: boolean = true;

  @property({ type: Boolean })
  accessor showLabels: boolean = true;

  @property({ type: String })
  accessor innerRadiusPercent: string = '55%';

  @property({ attribute: false })
  accessor valueFormatter: (value: number) => string = (val) => `${val}`;

  public static styles = donutStyles;

  public render(): TemplateResult {
    return renderChartDonut(this);
  }

  public async updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (
      this.chartInstance &&
      (changedProperties.has('data') ||
        changedProperties.has('valueFormatter') ||
        changedProperties.has('showLegend') ||
        changedProperties.has('showLabels') ||
        changedProperties.has('innerRadiusPercent'))
    ) {
      this.updateChart();
    }
  }

  protected hasChartData() { return this.data.length > 0; }
  protected dataSummary() { return this.data.map(item => `${item.name}: ${this.valueFormatter(item.value)}`).join(', '); }

  protected legendItems() {
    const colors = getEchartsSeriesColors(this.goBright);
    return this.showLegend ? this.data.map((item, index) => ({ name: item.name, color: item.color || colors[index % colors.length], value: this.valueFormatter(item.value) })) : [];
  }

  protected buildOption(): Record<string, any> {
    const themeColors = getThemeColors(this.goBright);
    const seriesColors = getEchartsSeriesColors(this.goBright);
    const fillAlpha = 0.85;

    const legendData: Array<{ name: string; itemStyle: { color: string } }> = [];

    const data = this.data.map((item, index) => {
      const color = item.color || seriesColors[index % seriesColors.length];
      legendData.push({ name: item.name, itemStyle: { color } });
      return {
        name: item.name,
        value: item.value,
        itemStyle: {
          color: hexToRgba(color, fillAlpha),
          borderColor: color,
          borderWidth: 1,
        },
        emphasis: {
          itemStyle: {
            color: hexToRgba(color, 1),
            borderColor: color,
            borderWidth: 1.5,
          },
        },
      };
    });

    const formatter = this.valueFormatter;

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const solidColor = params.data?.itemStyle?.borderColor || params.color;
          const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${solidColor};"></span>`;
          return `${marker}${params.name}: <strong>${formatter(params.value)}</strong> (${params.percent}%)`;
        },
      },
      legend: { data: legendData },
      series: [
        {
          type: 'pie',
          radius: [this.innerRadiusPercent, '76%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          padAngle: 2,
          itemStyle: {
            borderRadius: 4,
          },
          label: this.showLabels
            ? {
                show: true,
                formatter: this.chartWidth < 480 ? '{d}%' : '{b}: {d}%',
                position: 'outside',
                fontSize: 11,
                color: themeColors.textSecondary,
                textBorderColor: 'transparent',
              }
            : { show: false },
          data,
        },
      ],
    };
  }
}
